import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Dialog, ModuleFE_RoutingV2, ModuleFE_Thunderstorm, ModuleFE_Toaster, openContent } from '@nu-art/thunderstorm-frontend';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/TS_InputV2';
import { Dialog_Confirmation, ICONS, ResizableWrapper } from '@app/styles';
import { __stringify, voidFunction } from '@nu-art/ts-common';
import './Page_EHREmulator.scss';
import { Dialog_EHREmulatorFilters } from './Dialog_EHREmulatorFilters.js';
import { ModuleFE_Value } from '@app/hcs-frontend';
import { ModuleFE_Environment, ModuleFE_Facility, ModuleFE_Organization } from '@app/org-frontend';
import { ModuleFE_EncounterSimulation } from '../../_entity/encounter-simulation/index.js';
import { resolveValueDisplay } from '@app/hcs-shared';
import { EventsTimeline } from './EventsTimeline/index.js';
import { RoutePermissions } from '@nu-art/permissions-frontend';
import { ModuleFE_EHREmulator } from '../../_modules/ModuleFE_EHREmulator.js';
import { Dialog_DemoEventLabel } from './Dialog_DemoEventLabel.js';
import { Panel_SimulationDemoEventsEditor } from './Panel_SimulationDemoEventsEditor.js';
import { ENCOUNTER_EVENT_MINUTE_MS, getEncounterEventTimelineMs, resolveBugReportFileGcsLocation, } from './encounterEventUtils.js';
import { ModuleFE_SimulationDemoEvent } from '../../_entity/simulation-demo-events/index.js';
import { EHREmulator_DisplayEvent } from './EHREmulator_DisplayEvent.js';
import { EHREmulator_EncounterGroup } from './EHREmulator_EncounterGroup.js';
import { EHREmulator_RightPanelActionsHeader } from './EHREmulator_RightPanelActionsHeader.js';
/** Only call encounter-by-id API when the box looks like a full id (UUID or long Firestore-style `_id`). */
const ENCOUNTER_ID_SEARCH_BODY_PATTERN = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-zA-Z0-9_-]{20,})$/i;
const ENCOUNTER_SEARCH_DEBOUNCE_MS = 200;
const ENCOUNTER_SEARCH_MAX_WAIT_MS = 1000;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'km_sidebar_collapsed';
// ######################### Component #########################
export class Page_EHREmulator extends ComponentSync {
    /** Fixed height for the timeline strip when stacked above the advisor (chart is compact; strip can scroll if needed). */
    static DEFAULT_STACKED_TIMELINE_HEIGHT_PX = 120;
    static Route = {
        key: 'ehr-emulator',
        path: 'ehr-emulator',
        Component: Page_EHREmulator,
        modulesToAwait: [ModuleFE_Facility, ModuleFE_Organization, ModuleFE_Environment, ModuleFE_Value],
        enabled: RoutePermissions(ModuleFE_EHREmulator.permissions.Release),
    };
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.selectedFacilityIds ??= [];
        state.selectedEnvironmentIds ??= [];
        state.search ??= ModuleFE_RoutingV2.getQueryParameter('encounterId') ?? '';
        state.encountersWithEvents ??= [];
        state.loading ??= true;
        state.leftPanelWidth ??= 500;
        state.leftPanelCollapsed ??= localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
        state.isResizing ??= false;
        state.showSimulationDemoEventsEditor ??= false;
        state.encounterTimelineGroup ??= null;
        state.encounterTimelineCollapsed ??= false;
        state.encounterGroupCollapsedById ??= {};
        state.isFullscreen ??= false;
        return state;
    }
    async componentDidMount() {
        document.addEventListener('keydown', this.handleFullscreenKeyDown);
        const encounterId = this.state.search.trim();
        if (ENCOUNTER_ID_SEARCH_BODY_PATTERN.test(encounterId)) {
            this.logInfo(`ehr-emulator deep-link encounterId=${encounterId}`);
            await this.onEncounterIdSearch();
            return;
        }
        await this.loadEncountersWithEvents();
    }
    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
        document.removeEventListener('keydown', this.handleFullscreenKeyDown);
        this.rightPanelRef?.classList.remove('fullscreen');
        if (this.searchDebounceTimeout)
            clearTimeout(this.searchDebounceTimeout);
        this.clearEncounterIdSearchTimers();
    }
    //######################### Dispatcher #########################
    searchDebounceTimeout;
    encounterIdSearchTrailingTimer;
    encounterIdSearchMaxWaitTimer;
    encounterIdSearchBurstStart = 0;
    __onEHREmulatorFiltersChange = (data) => {
        const filtersChanged = data.selectedFacilityIds.join(',') !== this.state.selectedFacilityIds.join(',') ||
            data.selectedEnvironmentIds.join(',') !== this.state.selectedEnvironmentIds.join(',') ||
            data.search.trim() !== this.state.search.trim();
        this.setState({
            selectedFacilityIds: data.selectedFacilityIds,
            selectedEnvironmentIds: data.selectedEnvironmentIds,
            search: data.search,
            selectedTimeRange: data.selectedTimeRange,
        }, () => {
            // Debounce the filter search to avoid too many API calls
            if (filtersChanged) {
                if (this.searchDebounceTimeout) {
                    clearTimeout(this.searchDebounceTimeout);
                }
                this.searchDebounceTimeout = setTimeout(() => {
                    this.onFilterSearch();
                }, 500);
            }
        });
    };
    __onEncounterSimulationUpdated = (...params) => {
        const { encountersWithEvents } = this.state;
        if (!encountersWithEvents.length)
            return;
        const simulations = Array.isArray(params[1]) ? params[1] : [params[1]];
        const simulationsByEventId = new Map();
        for (const simulation of simulations) {
            const list = simulationsByEventId.get(simulation.eventId) ?? [];
            list.push(simulation);
            simulationsByEventId.set(simulation.eventId, list);
        }
        let updated = false;
        const updatedEncounters = encountersWithEvents.map(enc => ({
            ...enc,
            eventsWithSimulations: enc.eventsWithSimulations.map(item => {
                const eventId = item.event._id;
                const newSimulationsForEvent = simulationsByEventId.get(eventId) ?? [];
                if (!newSimulationsForEvent.length)
                    return item;
                const existingIds = new Set(item.simulations.map(s => s._id));
                const updatedSimulations = item.simulations.map((existingSim) => {
                    const updatedSim = newSimulationsForEvent.find((s) => s._id === existingSim._id);
                    return updatedSim ?? existingSim;
                });
                const newSimulations = newSimulationsForEvent.filter((s) => !existingIds.has(s._id));
                if (newSimulations.length > 0 || newSimulationsForEvent.some((s) => existingIds.has(s._id))) {
                    updated = true;
                }
                return { ...item, simulations: [...updatedSimulations, ...newSimulations] };
            }),
        }));
        if (updated) {
            this.setState({ encountersWithEvents: updatedEncounters });
        }
    };
    //######################### Logic #########################
    onDownloadBugReportFile = async (file) => {
        try {
            const { bucketName, objectPath } = resolveBugReportFileGcsLocation(file);
            const res = await ModuleFE_EHREmulator._v1.downloadBugReportFile({
                bucketName,
                objectPath,
                downloadFileName: file.name,
            }).executeSync();
            ModuleFE_Thunderstorm.downloadFile({ url: res.signedUrl, fileName: file.name });
        }
        catch (e) {
            this.logError(__stringify(e));
            ModuleFE_Toaster.toastError('Could not download file');
        }
    };
    loadEncountersWithEvents = async () => {
        this.setState({ loading: true });
        try {
            const encounters = await ModuleFE_EHREmulator._v1.getEventsWithSimulations({}).executeSync();
            this.setState({
                encountersWithEvents: encounters,
                loading: false,
            });
        }
        catch (e) {
            console.error('Failed to load encounters:', { error: e, message: e?.message, stack: e?.stack });
            ModuleFE_Toaster.toastError(`Failed to load encounters`);
            this.setState({ loading: false });
        }
    };
    clearEncounterIdSearchTimers = () => {
        if (this.encounterIdSearchTrailingTimer) {
            clearTimeout(this.encounterIdSearchTrailingTimer);
            this.encounterIdSearchTrailingTimer = undefined;
        }
        if (this.encounterIdSearchMaxWaitTimer) {
            clearTimeout(this.encounterIdSearchMaxWaitTimer);
            this.encounterIdSearchMaxWaitTimer = undefined;
        }
        this.encounterIdSearchBurstStart = 0;
    };
    /**
     * Debounced encounter fetch: 200ms trailing, 1000ms max wait while the value stays a matching id shape.
     * Non-id text never calls `getEventsWithSimulationsByEncounterId` (list is narrowed client-side only).
     */
    scheduleEncounterSearchFromInput = (valueTrimmed) => {
        if (this.encounterIdSearchTrailingTimer) {
            clearTimeout(this.encounterIdSearchTrailingTimer);
            this.encounterIdSearchTrailingTimer = undefined;
        }
        if (!valueTrimmed) {
            this.clearEncounterIdSearchTimers();
            this.encounterIdSearchTrailingTimer = setTimeout(() => {
                this.encounterIdSearchTrailingTimer = undefined;
                void this.onEncounterIdSearch();
            }, ENCOUNTER_SEARCH_DEBOUNCE_MS);
            return;
        }
        if (!ENCOUNTER_ID_SEARCH_BODY_PATTERN.test(valueTrimmed)) {
            this.clearEncounterIdSearchTimers();
            return;
        }
        if (!this.encounterIdSearchBurstStart) {
            this.encounterIdSearchBurstStart = Date.now();
            this.encounterIdSearchMaxWaitTimer = setTimeout(() => {
                this.encounterIdSearchMaxWaitTimer = undefined;
                this.encounterIdSearchBurstStart = 0;
                if (this.encounterIdSearchTrailingTimer) {
                    clearTimeout(this.encounterIdSearchTrailingTimer);
                    this.encounterIdSearchTrailingTimer = undefined;
                }
                void this.onEncounterIdSearch();
            }, ENCOUNTER_SEARCH_MAX_WAIT_MS);
        }
        this.encounterIdSearchTrailingTimer = setTimeout(() => {
            this.encounterIdSearchTrailingTimer = undefined;
            if (this.encounterIdSearchMaxWaitTimer) {
                clearTimeout(this.encounterIdSearchMaxWaitTimer);
                this.encounterIdSearchMaxWaitTimer = undefined;
            }
            this.encounterIdSearchBurstStart = 0;
            void this.onEncounterIdSearch();
        }, ENCOUNTER_SEARCH_DEBOUNCE_MS);
    };
    onEncounterIdSearch = async () => {
        const encounterId = this.state.search.trim();
        if (!encounterId) {
            await this.loadEncountersWithEvents();
            return;
        }
        if (!ENCOUNTER_ID_SEARCH_BODY_PATTERN.test(encounterId)) {
            this.setState({ loading: false });
            return;
        }
        this.setState({ loading: true });
        try {
            const encounters = await ModuleFE_EHREmulator._v1.getEventsWithSimulationsByEncounterId({
                encounterId,
            }).executeSync();
            this.setState({
                encountersWithEvents: encounters,
                loading: false,
            });
        }
        catch (e) {
            console.error('Failed to load encounters by encounter ID:', { error: e, message: e?.message });
            ModuleFE_Toaster.toastError(`Failed to load encounters`);
            this.setState({ loading: false });
        }
    };
    onFilterSearch = async () => {
        const { selectedEnvironmentIds, selectedFacilityIds } = this.state;
        const hasFilters = selectedEnvironmentIds.length > 0 || selectedFacilityIds.length > 0;
        if (!hasFilters) {
            await this.loadEncountersWithEvents();
            return;
        }
        this.setState({ loading: true });
        try {
            const encounters = await ModuleFE_EHREmulator._v1.getEventsWithSimulationsByFilters({
                environmentIds: selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : undefined,
                facilityIds: selectedFacilityIds.length > 0 ? selectedFacilityIds : undefined,
            }).executeSync();
            this.setState({
                encountersWithEvents: encounters,
                loading: false,
            });
        }
        catch (e) {
            console.error('Failed to load encounters by filters:', { error: e, message: e?.message });
            ModuleFE_Toaster.toastError(`Failed to load encounters`);
            this.setState({ loading: false });
        }
    };
    requestDemoEventLabel = async (defaultLabel) => {
        return await new Promise((resolve) => {
            ModuleFE_Dialog.show({
                content: (_jsx(Dialog_DemoEventLabel, { defaultLabel: defaultLabel, onCancel: () => {
                        ModuleFE_Dialog.close();
                        resolve(undefined);
                    }, onSubmit: (label) => {
                        ModuleFE_Dialog.close();
                        resolve(label);
                    } })),
            });
        });
    };
    onEventContextMenu = async (item, e) => {
        e.preventDefault();
        e.stopPropagation();
        const eventId = item.event._id;
        try {
            const extra = item.encounterData?.encounterExtraData;
            const gender = this.getPatientGender(extra);
            const age = extra?.age ?? 'N/A';
            const defaultLabel = `${gender} - ${age}`;
            const label = (await this.requestDemoEventLabel(defaultLabel) ?? '').trim();
            if (!label)
                return;
            await ModuleFE_SimulationDemoEvent.v1.upsert({ _id: eventId, eventId, label }).executeSync();
            ModuleFE_Toaster.toastSuccess('Marked as demo event');
            return;
        }
        catch (err) {
            console.error('Failed to toggle demo event', { eventId, error: err, message: err?.message });
            ModuleFE_Toaster.toastError('Failed to update demo event');
        }
    };
    getFilteredEncounterGroups = () => {
        const { encountersWithEvents, search, selectedTimeRange } = this.state;
        const searchTerm = search.trim().toLowerCase();
        const cutoffTime = selectedTimeRange?.duration ? Date.now() - selectedTimeRange.duration : 0;
        return encountersWithEvents
            .filter(enc => !searchTerm || (enc.encounterData._id ?? '').toLowerCase().includes(searchTerm))
            .map(enc => ({
            ...enc,
            eventsWithSimulations: selectedTimeRange?.duration
                ? enc.eventsWithSimulations.filter(item => getEncounterEventTimelineMs(item.event) >= cutoffTime)
                : enc.eventsWithSimulations,
        }))
            .filter(enc => enc.eventsWithSimulations.length > 0);
    };
    getPatientGender = (extraData) => {
        const patientGender = extraData?.gender ?? 'N/A';
        const gender = ModuleFE_Value.cache.unique(patientGender);
        return gender ? resolveValueDisplay(gender) : 'N/A';
    };
    //######################## Event Handlers - Simulation #########################
    onSimulationTitleChange = async (simulation, newTitle) => {
        await ModuleFE_EncounterSimulation.v1.upsert({ ...simulation, title: newTitle }).executeSync();
        this.setState({ editingSimulationId: undefined });
    };
    onSimulationTitleAccept = (simulation, newTitle) => {
        if (newTitle.trim() !== (simulation.title || '')) {
            this.onSimulationTitleChange(simulation, newTitle.trim());
        }
        else {
            this.setState({ editingSimulationId: undefined });
        }
    };
    onSimulationTitleCancel = () => {
        this.setState({ editingSimulationId: undefined });
    };
    onSimulationTitleDoubleClick = (simulationId) => {
        this.setState({ editingSimulationId: simulationId });
    };
    onCreateSimulation = async (item, e) => {
        e.stopPropagation();
        await this.createSimulationFromDisplayRow(item);
    };
    /**
     * Returns the display-event row for the latest event in the given minute bucket
     * (same minute bucketing as the event timeline markers).
     */
    getLatestDisplayEventRowForBar = (group, barStartMs) => {
        let best = null;
        let bestTimelineMs = -1;
        for (const ev of group.eventsWithSimulations) {
            const t = getEncounterEventTimelineMs(ev.event);
            const bucket = Math.floor(t / ENCOUNTER_EVENT_MINUTE_MS) * ENCOUNTER_EVENT_MINUTE_MS;
            if (bucket !== barStartMs) {
                continue;
            }
            if (t >= bestTimelineMs) {
                bestTimelineMs = t;
                best = { ...ev, encounterData: group.encounterData };
            }
        }
        return best;
    };
    handleEncounterGroupCollapseToggle = (group, encounterIdKey, wasCollapsed) => {
        const nextCollapsed = !wasCollapsed;
        this.setState((prev) => ({
            encounterGroupCollapsedById: {
                ...prev.encounterGroupCollapsedById,
                [encounterIdKey]: nextCollapsed,
            },
            encounterTimelineGroup: !nextCollapsed
                ? group
                : prev.encounterTimelineGroup?.encounterData?._id === encounterIdKey
                    ? null
                    : prev.encounterTimelineGroup,
            ...(!nextCollapsed ? { encounterTimelineCollapsed: false } : {}),
        }));
    };
    handleEncounterTimelineCreateSimulationForBar = async (barStartMs) => {
        const group = this.state.encounterTimelineGroup;
        if (!group) {
            return false;
        }
        const item = this.getLatestDisplayEventRowForBar(group, barStartMs);
        if (!item) {
            ModuleFE_Toaster.toastError('No event found for this time bucket.');
            return false;
        }
        return await this.createSimulationFromDisplayRow(item);
    };
    /** Creates a simulation from a display event row; updates list, launches advisor on success. */
    createSimulationFromDisplayRow = async (item) => {
        const encounterData = item.encounterData;
        try {
            const newSimulation = await ModuleFE_EncounterSimulation._v1.createEncounterSimulation({
                eventId: item.event._id,
                encounterId: encounterData._id,
                facilityId: encounterData.organizationMetadata.facilityId,
                environmentId: encounterData.organizationMetadata.environmentId,
            }).executeSync();
            this.setState((prevState) => ({
                encountersWithEvents: prevState.encountersWithEvents.map((enc) => ({
                    ...enc,
                    eventsWithSimulations: enc.eventsWithSimulations.map((ev) => ev.event._id === item.event._id
                        ? { ...ev, simulations: [...ev.simulations, newSimulation] }
                        : ev),
                })),
            }), () => {
                this.onLaunchSimulation(newSimulation);
            });
            return true;
        }
        catch (error) {
            console.error('Failed to create simulation:', {
                encounterId: encounterData._id,
                eventId: item.event._id,
                error: error?.message,
            });
            ModuleFE_Toaster.toastError('Failed to create simulation. Please try again.');
            return false;
        }
    };
    onLaunchSimulation = async (simulation) => {
        try {
            const response = await ModuleFE_EncounterSimulation._v1.getSimulationLaunchJwt({
                token: simulation.encounterId,
            }).executeSync();
            this.setState({
                selectedSimulation: {
                    ...simulation,
                    sessionJwt: response.jwt,
                    url: response.url,
                }
            });
        }
        catch (error) {
            console.error('Failed to launch simulation:', { simulationId: simulation._id, encounterId: simulation.encounterId, error: error?.message });
            ModuleFE_Toaster.toastError(`Could not launch simulation. Please try again.`);
        }
    };
    onDeleteSimulation = (simulation) => {
        Dialog_Confirmation.show({
            header: 'Are you sure?',
            content: _jsxs(_Fragment, { children: [_jsx("div", { children: "You are about to delete a simulation" }), _jsx("div", { children: simulation.title || simulation._id })] }),
            buttons: [
                {
                    label: 'Yes, Delete', isAsync: true, action: async () => {
                        await ModuleFE_EncounterSimulation.v1.delete({ _id: simulation._id }).executeSync();
                        const { encountersWithEvents, selectedSimulation } = this.state;
                        const updatedEncounters = encountersWithEvents.map(enc => ({
                            ...enc,
                            eventsWithSimulations: enc.eventsWithSimulations.map(item => ({
                                ...item,
                                simulations: item.simulations.filter(sim => sim._id !== simulation._id),
                            })),
                        }));
                        this.setState({
                            encountersWithEvents: updatedEncounters,
                            selectedSimulation: selectedSimulation?._id === simulation._id ? undefined : selectedSimulation,
                        });
                    }
                },
                { label: 'Cancel', isPreferred: true, action: voidFunction }
            ]
        });
    };
    hasActiveFilters = () => {
        const { selectedFacilityIds, selectedEnvironmentIds, search } = this.state;
        return selectedFacilityIds.length > 0 || selectedEnvironmentIds.length > 0 || search.trim().length > 0;
    };
    //######################## Event Handlers - Fullscreen #########################
    rightPanelRef = null;
    enterFullscreen = () => {
        this.rightPanelRef?.classList.add('fullscreen');
        this.setState({ isFullscreen: true });
    };
    exitFullscreen = () => {
        this.rightPanelRef?.classList.remove('fullscreen');
        this.setState({ isFullscreen: false });
    };
    toggleFullscreen = () => {
        if (this.state.isFullscreen)
            this.exitFullscreen();
        else
            this.enterFullscreen();
    };
    handleFullscreenKeyDown = (e) => {
        if (e.key === 'Escape' && this.state.isFullscreen)
            this.exitFullscreen();
    };
    //######################## Event Handlers - Resizing #########################
    handleResizeStart = (e) => {
        e.preventDefault();
        this.setState({ isResizing: true });
        document.addEventListener('mousemove', this.handleResizeMove);
        document.addEventListener('mouseup', this.handleResizeEnd);
    };
    handleResizeMove = (e) => {
        const minWidth = 500;
        const maxWidth = 1200;
        const newWidth = Math.min(maxWidth, Math.max(minWidth, e.clientX));
        this.setState({ leftPanelWidth: newWidth });
    };
    handleResizeEnd = () => {
        this.setState({ isResizing: false });
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
    };
    //######################## Event Handlers - Search #########################
    handleSearchChange = (value) => {
        const trimmed = value.trim();
        this.setState({ search: value }, () => {
            this.scheduleEncounterSearchFromInput(trimmed);
        });
    };
    //######################### Render #########################
    handleRightPanelCloseAdvisorAndTimeline = () => {
        if (this.state.isFullscreen)
            this.exitFullscreen();
        this.setState({
            selectedSimulation: undefined,
            encounterTimelineGroup: null,
            encounterTimelineCollapsed: false,
        });
    };
    render_RightPanel = () => {
        const { showSimulationDemoEventsEditor, encounterTimelineGroup, encounterTimelineCollapsed, selectedSimulation, } = this.state;
        if (showSimulationDemoEventsEditor) {
            return (_jsx(Panel_SimulationDemoEventsEditor, { onClose: () => this.setState({ showSimulationDemoEventsEditor: false }) }));
        }
        const hasAdvisorSession = !!selectedSimulation;
        const advisorIframe = hasAdvisorSession && selectedSimulation
            ? this.render_AdvisorFrame(selectedSimulation)
            : null;
        const hasTimeline = !!encounterTimelineGroup;
        const timeline = hasTimeline && encounterTimelineGroup ? (_jsx(EventsTimeline, { group: encounterTimelineGroup, onCreateSimulationForBar: this.handleEncounterTimelineCreateSimulationForBar, stackedWithAdvisor: hasAdvisorSession })) : null;
        const timelineExpanded = hasTimeline && !encounterTimelineCollapsed;
        if (!hasAdvisorSession && !hasTimeline) {
            return null;
        }
        const timelineWrapperStyle = hasAdvisorSession
            ? {
                height: timelineExpanded ? Page_EHREmulator.DEFAULT_STACKED_TIMELINE_HEIGHT_PX : 0,
                minHeight: 0,
                overflow: 'hidden',
            }
            : {
                flex: timelineExpanded ? 1 : 0,
                minHeight: 0,
                height: timelineExpanded ? undefined : 0,
                overflow: 'hidden',
            };
        return (_jsxs("div", { className: 'ehr-emulator__right-panel-stack', children: [_jsx(EHREmulator_RightPanelActionsHeader, { hasTimeline: hasTimeline, hasAdvisorSession: hasAdvisorSession, encounterTimelineCollapsed: !!encounterTimelineCollapsed, isFullscreen: !!this.state.isFullscreen, onToggleTimelineCollapse: () => this.setState((s) => ({ encounterTimelineCollapsed: !s.encounterTimelineCollapsed })), onCloseAdvisorAndTimeline: this.handleRightPanelCloseAdvisorAndTimeline, onToggleFullscreen: this.toggleFullscreen }), hasTimeline && (_jsx("div", { className: _className('ehr-emulator__right-panel-stack__timeline', !hasAdvisorSession && 'ehr-emulator__right-panel-stack__timeline--solo'), style: timelineWrapperStyle, children: timeline })), hasAdvisorSession && (_jsx(ResizableWrapper, { className: 'ehr-emulator__advisor-frame-resizable', storageKeySuffix: 'ehr-emulator-advisor-frame', children: _jsx("div", { className: 'ehr-emulator__advisor-frame-wrap', children: advisorIframe }) }))] }));
    };
    toggleLeftPanelCollapsed = () => {
        this.setState((prev) => {
            const next = !prev.leftPanelCollapsed;
            localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
            return { leftPanelCollapsed: next };
        });
    };
    render() {
        const { loading, leftPanelWidth, leftPanelCollapsed, isResizing } = this.state;
        const filteredEncounterGroups = this.getFilteredEncounterGroups();
        return (_jsxs(LL_H_C, { id: 'page__ehr-emulator', className: isResizing ? 'resizing' : '', children: [_jsxs("div", { className: _className('ehr-emulator__sidebar-wrapper', leftPanelCollapsed && 'is-collapsed'), children: [_jsx("div", { className: _className('ehr-emulator__left-panel', leftPanelCollapsed && 'collapsed'), style: leftPanelCollapsed ? undefined : { width: leftPanelWidth }, children: _jsxs("div", { className: 'ehr-emulator__left-panel-inner', style: { width: leftPanelWidth }, children: [_jsx("div", { className: 'ehr-emulator__left-panel-header', children: this.render_Header(filteredEncounterGroups.length) }), _jsx("div", { className: 'ehr-emulator__left-panel-content', children: loading ? this.render_Loading() : this.render_Results(filteredEncounterGroups) })] }) }), _jsx("button", { className: 'ehr-emulator__sidebar-toggle', onClick: this.toggleLeftPanelCollapsed, "aria-label": leftPanelCollapsed ? 'Expand encounters sidebar' : 'Collapse encounters sidebar', children: _jsx("div", { className: 'ehr-emulator__sidebar-toggle__icon', children: _jsx("svg", { viewBox: '0 0 11 11', children: _jsx("polyline", { points: '7,1.5 3,5.5 7,9.5' }) }) }) })] }), !leftPanelCollapsed && (_jsx("div", { className: 'ehr-emulator__resizer', onMouseDown: this.handleResizeStart })), _jsx("div", { className: 'ehr-emulator__right-panel', ref: el => this.rightPanelRef = el, children: this.render_RightPanel() })] }));
    }
    render_Header = (encounterCount) => {
        const hasFilters = this.hasActiveFilters();
        const { showSimulationDemoEventsEditor } = this.state;
        return _jsxs(LL_V_L, { className: 'ehr-emulator__header', children: [_jsxs(LL_H_C, { className: 'ehr-emulator__header__top-row', children: [_jsxs("div", { className: 'ehr-emulator__header__title', children: [_jsx("span", { className: 'ehr-emulator__header__title-text', children: "Encounters" }), _jsxs("span", { className: 'ehr-emulator__header__count', children: ["(", encounterCount, ")"] })] }), _jsxs(LL_H_C, { className: 'ehr-emulator__header__actions', children: [_jsx(Button, { variant: 'tertiary', className: _className('ehr-emulator__header__demo-events-button', showSimulationDemoEventsEditor && 'active'), onClick: () => this.setState((s) => ({
                                        showSimulationDemoEventsEditor: !s.showSimulationDemoEventsEditor,
                                    })), ...openContent.tooltip.top('demo-events-editor-tooltip', showSimulationDemoEventsEditor ? 'Close Demo Encounters Editor' : 'Edit Demo Encounters', { offset: 8 }), children: _jsx(ICONS.edit.component, {}) }), _jsx(Button, { variant: 'tertiary', className: _className('ehr-emulator__header__filter-button', hasFilters && 'filter-active'), ...openContent.popUpV2.bottom({
                                        content: _jsx(Dialog_EHREmulatorFilters, { selectedFacilityIds: this.state.selectedFacilityIds, selectedEnvironmentIds: this.state.selectedEnvironmentIds, search: this.state.search, selectedTimeRange: this.state.selectedTimeRange }),
                                        id: 'ehr-emulator__filters_popup'
                                    }), ...openContent.tooltip.top('filter-encounters-tooltip', 'Filter encounters', { offset: 8 }), children: _jsx(ICONS.advisor_avn_filter.component, {}) })] })] }), _jsxs(LL_H_C, { className: 'ehr-emulator__header__search-row', children: [_jsxs("div", { className: 'ehr-emulator__header__search-bar', children: [_jsx(ICONS.advisor_v5_search.component, {}), _jsx(TS_InputV2, { type: "text", value: this.state.search, onChange: (value) => this.handleSearchChange(value), placeholder: "Search Encounter", className: 'ehr-emulator__header__search-input', style: { fontFamily: 'monospace', fontSize: 10 } }), this.state.search && (_jsx("div", { className: 'ehr-emulator__header__search-clear', onClick: () => this.handleSearchChange(''), children: _jsx(ICONS.x.component, {}) }))] }), _jsx(Button, { variant: 'tertiary', className: 'ehr-emulator__header__refresh-button', onClick: this.loadEncountersWithEvents, disabled: this.state.loading, ...openContent.tooltip.top('refresh-encounters-tooltip', 'Refresh encounters', { offset: 8 }), children: _jsx(ICONS.sync.component, {}) })] })] });
    };
    render_Loading = () => {
        return _jsx("div", { className: 'ehr-emulator__loading', children: "Loading encounters..." });
    };
    //######################### Render Simulation Frame #########################
    render_AdvisorFrame = (simulation) => {
        if (!simulation.url)
            return null;
        return _jsx("iframe", { className: 'match_parent', src: simulation.url, allow: "clipboard-write" }, simulation.url);
    };
    //######################### Render Encounters #########################
    render_Results = (encounterGroups) => {
        if (encounterGroups.length === 0)
            return _jsxs("div", { className: 'ehr-emulator__results-empty', children: [_jsx("div", { className: 'ehr-emulator__results-empty', children: "No results found" }), _jsx("div", { className: 'ehr-emulator__results-empty__hint', children: this.hasActiveFilters()
                            ? 'Try adjusting your filters or click Search to query the server.'
                            : 'No events available.' })] });
        return _jsx("div", { className: 'ehr-emulator__results', children: encounterGroups.map((group) => {
                const encounterIdKey = group.encounterData._id ?? '';
                const { encounterTimelineGroup, selectedSimulation, encounterGroupCollapsedById } = this.state;
                return (_jsx(EHREmulator_EncounterGroup, { group: group, collapsed: encounterGroupCollapsedById?.[encounterIdKey] ?? true, isEncounterSelected: (encounterTimelineGroup?.encounterData?._id ?? '') === encounterIdKey ||
                        (selectedSimulation?.encounterId ?? '') === encounterIdKey, onCollapseToggle: (wasCollapsed) => this.handleEncounterGroupCollapseToggle(group, encounterIdKey, wasCollapsed), renderDisplayEvent: (item) => (_jsx(EHREmulator_DisplayEvent, { item: item, editingSimulationId: this.state.editingSimulationId, selectedSimulationId: this.state.selectedSimulation?._id, onEventContextMenu: this.onEventContextMenu, onCreateSimulation: this.onCreateSimulation, onDownloadBugReportFile: this.onDownloadBugReportFile, onSimulationTitleAccept: this.onSimulationTitleAccept, onSimulationTitleCancel: this.onSimulationTitleCancel, onSimulationTitleDoubleClick: this.onSimulationTitleDoubleClick, onDeleteSimulation: this.onDeleteSimulation, onLaunchSimulation: this.onLaunchSimulation }, item.event._id)) }, encounterIdKey));
            }) });
    };
}
//# sourceMappingURL=Page_EHREmulator.js.map